import { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Check, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/marketing/components/ui/table";
import { Badge } from "@/marketing/components/ui/badge";
import type { Influencer, TabId } from "@/marketing/models/influencer";
import { REGION_OPTIONS, DISCOVERY_STATUS_COLORS } from "@/marketing/models/influencer";
import { formatNumber } from "@/marketing/lib/formatters";
import { getSocialMediaUrl } from "@/marketing/lib/socialMediaUrls";
import { Checkbox } from "@/marketing/components/ui/checkbox";
import { useListStore } from "@/marketing/stores/useListStore";
import { INFLUENCER_KEYS } from "@/marketing/infrastructure/influencer/utils/keys";
import { influencerAPIClient } from "@/marketing/infrastructure/influencer/influencerAPIClient";

function SelectButton({
  influencer,
  isSelected,
}: {
  influencer: Influencer;
  isSelected: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const activeListId = useListStore((s) => s.activeListId);
  const queryClient = useQueryClient();

  if (isSelected) {
    const handleDeselect = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLoading(true);
      try {
        if (!activeListId) return;
        await influencerAPIClient.removeListMember(activeListId, influencer.id);
        queryClient.invalidateQueries({
          queryKey: INFLUENCER_KEYS.listMembers(activeListId),
          refetchType: "all",
        });
        queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
        queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.all });
      } catch (err) {
        console.error("Failed to deselect influencer:", err);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <button
        onClick={handleDeselect}
        disabled={isLoading}
        className={`tw-absolute -tw-bottom-1 -tw-right-1 tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-ring-2 tw-ring-white ${
          isLoading ? "tw-bg-neutral-400" : "tw-bg-green-600"
        }`}
        aria-label={`Deselect ${influencer.displayName}`}
      >
        {isLoading ? (
          <Loader2 className="tw-h-[11px] tw-w-[11px] tw-animate-spin tw-text-white" />
        ) : (
          <Check className="tw-h-[11px] tw-w-[11px] tw-text-white" />
        )}
      </button>
    );
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!activeListId) return;

    setIsLoading(true);
    try {
      await influencerAPIClient.selectInfluencer(influencer.id, activeListId);
      queryClient.invalidateQueries({
        queryKey: INFLUENCER_KEYS.listMembers(activeListId),
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: INFLUENCER_KEYS.all });
    } catch (err) {
      console.error("Failed to select influencer:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`tw-absolute -tw-bottom-1 -tw-right-1 tw-flex tw-h-[18px] tw-w-[18px] tw-items-center tw-justify-center tw-rounded-full tw-ring-2 tw-ring-white ${
        isLoading ? "tw-bg-neutral-400" : "tw-bg-neutral-900"
      }`}
      aria-label={`Select ${influencer.displayName}`}
    >
      {isLoading ? (
        <Loader2 className="tw-h-[11px] tw-w-[11px] tw-animate-spin tw-text-white" />
      ) : (
        <Plus className="tw-h-[11px] tw-w-[11px] tw-text-white" />
      )}
    </button>
  );
}

interface InfluencerTableProps {
  data: Influencer[];
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  activeTab?: TabId;
  selectedIds?: Set<string>;
  onRowClick?: (influencer: Influencer) => void;
  checkedIds?: Set<string>;
  onToggleOne?: (id: string) => void;
  onToggleAll?: (pageIds: string[]) => void;
}

function AvatarWithFallback({
  src,
  displayName,
}: {
  src?: string | undefined;
  displayName: string;
}) {
  const [hasError, setHasError] = useState(false);
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (!src || hasError) {
    return (
      <div className="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-xs tw-font-medium">
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={displayName}
      className="tw-h-9 tw-w-9 tw-shrink-0 tw-rounded-full tw-object-cover"
      onError={() => setHasError(true)}
    />
  );
}

export function InfluencerTable({
  data,
  pageCount,
  pageIndex,
  pageSize,
  onPageChange,
  activeTab = "discover",
  selectedIds,
  onRowClick,
  checkedIds,
  onToggleOne,
  onToggleAll,
}: InfluencerTableProps) {
  const showCheckbox =
    (activeTab === "discover" || activeTab === "selected") &&
    !!checkedIds &&
    !!onToggleOne &&
    !!onToggleAll;
  const pageIds = useMemo(() => data.map((d) => d.id), [data]);
  const allChecked =
    showCheckbox &&
    pageIds.length > 0 &&
    pageIds.every((id) => checkedIds!.has(id));

  const columns = useMemo<ColumnDef<Influencer>[]>(
    () => [
      ...(showCheckbox
        ? [
            {
              id: "select",
              size: 40,
              header: () => (
                <Checkbox
                  checked={allChecked}
                  onCheckedChange={() => onToggleAll!(pageIds)}
                  aria-label="Select all"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              ),
              cell: ({ row }: { row: { original: Influencer } }) => (
                <Checkbox
                  checked={checkedIds!.has(row.original.id)}
                  onCheckedChange={() => onToggleOne!(row.original.id)}
                  aria-label={`Select ${row.original.displayName}`}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                />
              ),
            } as ColumnDef<Influencer>,
          ]
        : []),
      {
        accessorKey: "displayName",
        header: "Creator",
        size: 320,
        cell: ({ row }) => {
          const { displayName, handle, profilePictureUrl } = row.original;
          return (
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-relative tw-shrink-0">
                <AvatarWithFallback
                  src={profilePictureUrl}
                  displayName={displayName}
                />
                {activeTab !== "rejected" && (
                  <SelectButton
                    influencer={row.original}
                    isSelected={selectedIds?.has(row.original.id) ?? false}
                  />
                )}
              </div>
              <div className="tw-min-w-0">
                <p className="tw-truncate tw-text-sm tw-font-medium">{displayName}</p>
                <a
                  href={getSocialMediaUrl(row.original.platform, handle)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="handle-link tw-text-xs tw-text-[#2563EB] hover:tw-underline"
                >
                  @{handle}
                </a>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "followersCount",
        header: "Followers",
        size: 130,
        cell: ({ row }) => (
          <span className="tw-text-sm tw-tabular-nums tw-text-right tw-block">
            {formatNumber(row.original.followersCount)}
          </span>
        ),
      },
      {
        accessorKey: "engagementRate",
        header: "Eng. Rate",
        size: 130,
        cell: ({ row }) => {
          const rate = row.original.engagementRate;
          if (rate == null) {
            return (
              <span className="tw-text-sm tw-text-muted-foreground tw-text-right tw-block">
                -
              </span>
            );
          }
          return (
            <span
              className="tw-text-sm tw-font-medium tw-tabular-nums tw-text-right tw-block"
              style={{ color: rate >= 5 ? "#16a34a" : "#737373" }}
            >
              {rate.toFixed(2)}%
            </span>
          );
        },
      },
      {
        accessorKey: "countrySignals",
        header: "Region",
        size: 100,
        cell: ({ row }) => {
          const signals = row.original.countrySignals ?? [];
          if (!signals.length) {
            return <span className="tw-text-sm tw-text-muted-foreground">-</span>;
          }
          const region = REGION_OPTIONS.find((r) => r.value === signals[0]);
          if (!region) {
            return (
              <Badge variant="outline" className="tw-font-normal">
                {signals[0]}
              </Badge>
            );
          }
          return (
            <Badge variant="outline" className="tw-font-normal">
              {region.flag} {region.label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "emails",
        header: "Email",
        size: 200,
        cell: ({ row }) => {
          const emails = row.original.emails ?? [];
          if (!emails.length) {
            return <span className="tw-text-sm tw-text-muted-foreground">-</span>;
          }
          return (
            <div className="tw-flex tw-items-center tw-gap-1 tw-min-w-0">
              <span className="tw-text-sm tw-truncate" title={emails[0]}>
                {emails[0]}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(emails[0] ?? "");
                  toast.success("Email copied to clipboard");
                }}
                className="tw-shrink-0 tw-rounded tw-p-0.5 tw-text-muted-foreground hover:tw-text-foreground hover:tw-bg-muted"
                aria-label="Copy email"
              >
                <Copy className="tw-h-3 tw-w-3" />
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: "discoveryStatus",
        header: "Status",
        size: 110,
        cell: ({ row }) => {
          const status = row.original.discoveryStatus;
          const colors = DISCOVERY_STATUS_COLORS[status];
          if (!colors) {
            return <Badge variant="outline">{status}</Badge>;
          }
          return (
            <Badge
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
              }}
              className="tw-gap-1 tw-font-normal"
            >
              <span
                className="tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-full"
                style={{ backgroundColor: colors.dot }}
              />
              {status}
            </Badge>
          );
        },
      },
    ],
    [
      activeTab,
      selectedIds,
      showCheckbox,
      checkedIds,
      allChecked,
      pageIds,
      onToggleOne,
      onToggleAll,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination: { pageIndex, pageSize },
    },
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater({ pageIndex, pageSize });
        onPageChange(newState.pageIndex);
      }
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="tw-rounded-md tw-border">
      <Table className="tw-table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="tw-bg-neutral-50">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{
                    width:
                      header.column.columnDef.size !== undefined
                        ? header.column.columnDef.size
                        : "auto",
                  }}
                  className={
                    header.column.id === "followersCount" ||
                    header.column.id === "engagementRate"
                      ? "tw-text-right"
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={
                  onRowClick ? "tw-cursor-pointer hover:tw-bg-muted/50" : undefined
                }
                onClick={() => {
                  onRowClick?.(row.original);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      width:
                        cell.column.columnDef.size !== undefined
                          ? cell.column.columnDef.size
                          : "auto",
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="tw-h-24 tw-text-center">
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-text-muted-foreground">
                  <p className="tw-text-sm tw-font-medium">No results found</p>
                  <p className="tw-text-xs">
                    Try adjusting your filters to find more influencers.
                  </p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
